import Phaser from "phaser";
import type { GameApp } from "../app/GameApp";
import { mountOverlay, type DomOverlay } from "@/ui/domHelpers";
import {
  CARD_CATALOG, CLASS_DEFS, tavernCardCost, describeApplyStatus, describeEnhancement,
  type CardDef, type CardEffect, type DomainId, type Stat,
} from "../core/roscha";

const STAT_SHORT: Record<Stat, string> = {
  strength: "СИЛ", dexterity: "ЛОВ", intelligence: "ИНТ", wisdom: "МУД", constitution: "ТЕЛ",
};

const RARITY_LABEL: Record<CardDef["rarity"], string> = {
  common: "Обычная",
  uncommon: "Необычная",
  rare: "Редкая",
  epic: "Эпическая",
};

interface CardRefs {
  card: CardDef;
  cost: number;
  buyBtn: HTMLButtonElement;
}

export class TavernOverlay extends Phaser.Scene {
  private app!: GameApp;
  private assortment: CardDef[] = [];
  private overlay?: DomOverlay;
  private goldEl?: HTMLElement;
  private cardRefs: CardRefs[] = [];
  private unsubWorld?: () => void;
  private onWake = () => this.refresh();

  constructor() {
    super("TavernOverlay");
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;
    this.rollAssortment();
    this.mount();
    this.refresh();
    this.unsubWorld = this.app.onWorldState(() => this.refresh());
    this.events.on(Phaser.Scenes.Events.WAKE, this.onWake);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private rollAssortment() {
    const hero = this.app.getWorldState()?.hero;
    if (!hero?.classId) return;
    const classIds = Object.values(CARD_CATALOG)
      .filter((c) => c.classId === hero.classId)
      .map((c) => c.id);
    const commonIds = Object.values(CARD_CATALOG)
      .filter((c) => c.classId == null)
      .map((c) => c.id);
    const pool = Array.from(new Set([...classIds, ...commonIds]))
      .map((id) => CARD_CATALOG[id])
      .filter((c): c is CardDef => c != null);
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    this.assortment = shuffled.slice(0, 4);
  }

  private mount() {
    const hero = this.app.getWorldState()?.hero;
    if (!hero) return;

    const cardsHtml =
      this.assortment.length === 0
        ? `<p class="tavern__empty">Сегодня в продаже пусто — торговец не нашёл карт для твоего класса.</p>`
        : this.assortment
            .map((card) => {
              const cost = tavernCardCost(card.rarity, hero.reputationUpgrades);
              return renderGameCard(card, hero.stats, cost, undefined, hero.domainId ?? undefined);
            })
            .join("");

    const bodyHtml = `
      <div class="dom-resource-row">
        <span><span class="icon-gold"></span> <strong data-bind="gold">0</strong></span>
      </div>
      <div class="tavern__grid">${cardsHtml}</div>
    `;

    this.overlay = mountOverlay(this, {
      title: "Таверна",
      subtitle: "Купи карты своего домена",
      bodyHtml,
      onClose: () => this.app.openTown(),
      inBody: true,
    });

    const root = this.overlay.root;
    this.goldEl = root.querySelector<HTMLElement>('[data-bind="gold"]') ?? undefined;
    this.cardRefs = this.assortment.map((card) => {
      const cost = tavernCardCost(card.rarity, hero.reputationUpgrades);
      const buyBtn = root.querySelector<HTMLButtonElement>(`.tavern__buy[data-card-id="${card.id}"]`)!;
      buyBtn.addEventListener("click", () => {
        if (buyBtn.disabled) return;
        this.app.buyTavernCard(card.id, cost);
      });
      return { card, cost, buyBtn };
    });
  }

  private refresh() {
    const hero = this.app.getWorldState()?.hero;
    if (!hero || !this.overlay) return;

    const gold = hero.inventory.resources.gold ?? 0;
    if (this.goldEl) this.goldEl.textContent = String(gold);
    for (const { cost, buyBtn } of this.cardRefs) {
      const canBuy = gold >= cost;
      buyBtn.disabled = !canBuy;
      buyBtn.setAttribute("data-disabled", String(!canBuy));
    }
  }

  private cleanup() {
    this.events.off(Phaser.Scenes.Events.WAKE, this.onWake);
    this.unsubWorld?.();
    this.unsubWorld = undefined;
    this.overlay = undefined;
    this.cardRefs = [];
    this.goldEl = undefined;
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
}

function classMeta(card: CardDef): { cls: string; label: string } {
  if (card.classId == null) return { cls: "game-card--d-common", label: "Общая" };
  return { cls: "game-card--d-class", label: CLASS_DEFS[card.classId].name };
}

export function renderGameCard(card: CardDef, stats: import("../core/roscha").Stats, cost?: number, count?: number, heroDomainId?: DomainId): string {
  const kindClass = `game-card--${card.kind}`;
  const rarityClass = `game-card--r-${card.rarity}`;
  const domain = classMeta(card);
  const hopeFormula = effectsToText(card.hope.effects, stats);
  const fearFormula = effectsToText(card.fear.effects, stats);
  const countBadge = count && count > 1 ? `<span class="game-card__count">×${count}</span>` : "";
  const costStr = card.hope.cost === card.fear.cost ? `${card.hope.cost}` : `${card.hope.cost} / ${card.fear.cost}`;
  const enhLine = (side: "hope" | "fear") => {
    const text = heroDomainId ? describeEnhancement(card, heroDomainId, side) : "";
    return text
      ? `<div class="game-card__section-enh game-card--d-${heroDomainId}"><span class="game-card__enh-dot"></span><span class="game-card__enh-text">${esc(text)}</span></div>`
      : "";
  };
  const buyBtn = cost != null
    ? `<button class="dom-btn dom-btn--success tavern__buy" data-card-id="${card.id}" data-cost="${cost}">Купить · ${cost} <span class="icon-gold icon-gold--sm"></span></button>`
    : "";
  return `
    <div class="game-card ${domain.cls} ${kindClass} ${rarityClass}">
      ${countBadge}
      <div class="game-card__frame">
        <div class="game-card__head">
          <div class="game-card__meta">
            <span class="game-card__domain">${domain.label}</span>
            <span class="game-card__rarity">${RARITY_LABEL[card.rarity]}</span>
          </div>
          <span class="game-card__name">${card.name}</span>
        </div>
        <div class="game-card__body">
          <div class="game-card__section game-card__section--hope">
            <span class="game-card__section-label">Надежда</span>
            <span class="game-card__formula">${hopeFormula}</span>
            ${enhLine("hope")}
          </div>
          <div class="game-card__section game-card__section--fear">
            <span class="game-card__section-label">Страх</span>
            <span class="game-card__formula">${fearFormula}</span>
            ${enhLine("fear")}
          </div>
        </div>
        <div class="game-card__footer">
          <span class="game-card__cost-label">Стоимость</span>
          <strong class="game-card__cost-value">${costStr}</strong>
        </div>
      </div>
      ${buyBtn}
    </div>
  `;
}

function effectsToText(effects: CardEffect[], stats: import("../core/roscha").Stats): string {
  const parts: string[] = [];
  for (const e of effects) {
    parts.push(effectLine(e, stats));
  }
  return parts.filter((s) => s.length > 0).join("<br>");
}

function effectLine(e: CardEffect, stats: import("../core/roscha").Stats): string {
  switch (e.type) {
    case "damage": return `Урон ${formula(e.base, e.dice, e.statScale, e.levelScale)}${rangeSuffix(e.base, e.dice, e.statScale, e.levelScale, stats)}`;
    case "heal":   return `Лечение ${formula(e.base, e.dice, e.statScale, e.levelScale)}${rangeSuffix(e.base, e.dice, e.statScale, e.levelScale, stats)}`;
    case "draw":   return `+${e.count} карт`;
    case "bonusAttack": return `${e.scope === "nextAttack" ? "След. атака" : "Все атаки"}: +${STAT_SHORT[e.bonusFromStat]}`;
    case "armorDebuff": return e.value < 0 ? `+${-e.value} армор` : `−${e.value} армор цели`;
    case "bonusDraw": return `При крите: +${e.count} карт`;
    case "armorBuff": return `+${formula(e.base, e.dice, e.statScale)} армора${rangeSuffix(e.base, e.dice, e.statScale, undefined, stats)}`;
    case "selfDamage": return `Себе: −${formula(e.base, e.dice, e.statScale)} HP`;
    case "applyStatus": return describeApplyStatus(e);
  }
}

function formula(base: number, dice?: { count: number; sides: number }, scale?: { stat: Stat; mult: number }, levelScale?: { mult: number }): string {
  const parts: string[] = [];
  if (base > 0) parts.push(String(base));
  if (dice) parts.push(`${dice.count}d${dice.sides}`);
  if (scale) parts.push(scale.mult > 1 ? `${scale.mult}×${STAT_SHORT[scale.stat]}` : STAT_SHORT[scale.stat]);
  if (levelScale) parts.push(levelScale.mult > 1 ? `${levelScale.mult}×Ур` : "Ур");
  return parts.join(" + ");
}

function rangeSuffix(base: number, dice?: { count: number; sides: number }, scale?: { stat: Stat; mult: number }, levelScale?: { mult: number }, stats?: import("../core/roscha").Stats): string {
  if (!stats) return "";
  let min = base, max = base;
  if (dice) { min += dice.count; max += dice.count * dice.sides; }
  if (scale) { const v = (stats[scale.stat] ?? 0) * scale.mult; min += v; max += v; }
  if (levelScale) { min += levelScale.mult; max += levelScale.mult; }
  return min === max ? ` (${min})` : ` (${min}–${max})`;
}
